import React, { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cleanNumberInput } from '../../../core/helpers';
import * as LoginManager from '../../../reducers/ui/login';
import { Button } from '../../../components/buttons/Button';
import * as Styles from './Styles';
import { useLifecycles } from 'react-use';

type Props = {};

export const TwoFactor = (_props: Props) => {
	const dispatch = useDispatch();

	const confirmCode = useSelector(LoginManager.Selectors.getConfirmCode);

	const setConfirmCodeDigit = useCallback(
		(pos: number, digit: string) =>
			dispatch(
				LoginManager.Actions.setConfirmCodeDigit({
					pos,
					digit,
				})
			),
		[dispatch]
	);

	const setStep = useCallback(
		(step: 'form' | 'confirm') =>
			dispatch(LoginManager.Actions.setStep(step)),
		[dispatch]
	);

	const digit1Ref = useRef<HTMLInputElement>(null);
	const digit2Ref = useRef<HTMLInputElement>(null);
	const digit3Ref = useRef<HTMLInputElement>(null);
	const digit4Ref = useRef<HTMLInputElement>(null);
	const digit5Ref = useRef<HTMLInputElement>(null);
	const digit6Ref = useRef<HTMLInputElement>(null);

	const allRefs = useMemo(
		() => [
			digit1Ref,
			digit2Ref,
			digit3Ref,
			digit4Ref,
			digit5Ref,
			digit6Ref,
		],
		[digit1Ref, digit2Ref, digit3Ref, digit4Ref, digit5Ref, digit6Ref]
	);

	const onPaste = useCallback(
		(e?: React.ClipboardEvent<HTMLInputElement>) => {
			e?.preventDefault();

			e?.clipboardData.items[0]?.getAsString((item) => {
				if (!parseInt(item) || item.length !== 6) return;

				setConfirmCodeDigit(0, item[0]);
				setConfirmCodeDigit(1, item[1]);
				setConfirmCodeDigit(2, item[2]);
				setConfirmCodeDigit(3, item[3]);
				setConfirmCodeDigit(4, item[4]);
				setConfirmCodeDigit(5, item[5]);
			});
		},
		[setConfirmCodeDigit]
	);

	const setDigit = useCallback(
		async (pos: number, value: string) => {
			setConfirmCodeDigit(pos, value.charAt(value.length - 1));

			value && allRefs[pos + 1]?.current?.focus();

			const check = confirmCode.map((el, idx) => {
				if (idx === pos) {
					return value;
				}

				return el;
			});

			if (check.every((el) => !!el)) {
				await LoginManager.sendConfirm();

				allRefs.forEach((ref) => {
					ref.current?.blur();
				});
			}
		},
		[setConfirmCodeDigit, allRefs, confirmCode]
	);

	useLifecycles(
		() => {
			digit1Ref.current?.focus();
		},
		() => {
			LoginManager.clearCode();
		}
	);

	return (
		<>
			<Styles.ConfirmTopFont>
				Autoryzacja dwuetapowa
			</Styles.ConfirmTopFont>
			<Styles.ConfirmBottomFont>
				Aby dokończyć logowanie należy podać kod wysłany na nr. telefonu
				połączony z tym kontem.
			</Styles.ConfirmBottomFont>
			<Styles.PinContainer>
				<input
					type="number"
					onChange={async (e) => await setDigit(0, e.target.value)}
					value={confirmCode[0]}
					onPaste={(e) => onPaste(e)}
					ref={digit1Ref}
					onKeyDown={cleanNumberInput}
				/>
				<input
					type="number"
					onChange={async (e) => await setDigit(1, e.target.value)}
					value={confirmCode[1]}
					onPaste={() => onPaste()}
					ref={digit2Ref}
					onKeyDown={cleanNumberInput}
				/>
				<input
					type="number"
					onChange={async (e) => await setDigit(2, e.target.value)}
					value={confirmCode[2]}
					onPaste={() => onPaste()}
					ref={digit3Ref}
					onKeyDown={cleanNumberInput}
				/>
				<input
					type="number"
					onChange={async (e) => await setDigit(3, e.target.value)}
					value={confirmCode[3]}
					onPaste={() => onPaste()}
					ref={digit4Ref}
					onKeyDown={cleanNumberInput}
				/>
				<input
					type="number"
					onChange={async (e) => await setDigit(4, e.target.value)}
					value={confirmCode[4]}
					onPaste={() => onPaste()}
					ref={digit5Ref}
					onKeyDown={cleanNumberInput}
				/>
				<input
					type="number"
					onChange={async (e) => await setDigit(5, e.target.value)}
					value={confirmCode[5]}
					onPaste={() => onPaste()}
					ref={digit6Ref}
					onKeyDown={cleanNumberInput}
				/>
			</Styles.PinContainer>
			<Button
				accent="#fff"
				color="#7bb6b3"
				label="wstecz"
				onClick={() => setStep('form')}
				variant="solid"
				style={{
					height: '45px',
					width: '250px',
					margin: '0 auto',
				}}
			/>
		</>
	);
};
